import { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { setError as setAbsencesError } from '../../../../@paco/redux/absences/absencesReducer';
import {
    approveExchangeRequest,
    approveShiftExchange,
    declineExchangeRequest,
    declineShiftExchange,
    fetchExchangeRequests,
} from '../../../../@paco/redux/exchanges/exchangesActions';
import { useTypedDispatch, useTypedSelector } from '../../../../@paco/redux/store';
import { setToast } from '../../../../@paco/redux/toasts/toastsReducer';
import { ToastType } from '../../../../@paco/types';
import { getRangeFromDate } from '../../../../helpers/date';
import { setEmployeeSearch, setFilter } from '../../../../redux/filter/filterActions';
import { AppFilter } from '../../../../redux/filter-ts/filterModels';
import { Reducers } from '../../../../redux/reducers';
import { setStartAndEndDate } from '../../../../redux/weekNavigator/weekNavigatorActions';
import ExchangeRequestsContainer from '../../containers/ExchangeRequestsContainer/ExchangeRequestsContainer';
import sortRequestsByDate from './helpers/sortRequestsByDate';

const ConnectedExchangeRequests = () => {
    const dispatch = useTypedDispatch();

    const {
        isLoading,
        error: exchangesError,
        exchangeRequests,
        shiftExchanges,
    } = useSelector((state: Reducers) => state.exchangesReducer);
    const filter = useSelector((state: Reducers) => state.filterReducer.filter);
    const { departmentOptions } = useTypedSelector(state => state.globalFiltersReducer);
    const { permissions } = useTypedSelector(state => state.authenticatedUserReducer);
    const { mode } = useSelector((state: Reducers) => state.weekNavigatorReducer);

    const exchanges = useMemo(() => sortRequestsByDate([...exchangeRequests, ...shiftExchanges]), [exchangeRequests, shiftExchanges]);

    const getExchanges = async () => {
        await dispatch(fetchExchangeRequests(filter));
    };

    useEffect(() => {
        getExchanges();
    }, [departmentOptions]);

    const onApproveExchange = async (exchangeId: string, userId: string) => {
        await dispatch(approveExchangeRequest(exchangeId, userId));
        getExchanges();
    };

    const onDeclineExchange = async (exchangeId: string) => {
        await dispatch(declineExchangeRequest(exchangeId));
        getExchanges();
    };

    const onApproveShiftExchange = async (shiftExchangeId: string) => {
        await dispatch(approveShiftExchange(shiftExchangeId));
        getExchanges();
    };

    const onDeclineShiftExchange = async (shiftExchangeId: string) => {
        await dispatch(declineShiftExchange(shiftExchangeId));
        getExchanges();
    };

    const onSearchBarChange = async (search: string) => {
        await dispatch(setEmployeeSearch(search));
        getExchanges();
    };

    const onExchangeNavButtonClick = (date: Date) => {
        const days = getRangeFromDate(date, mode);
        dispatch(setStartAndEndDate(days[0], days[0], days[days.length - 1]));
    };

    const onFilterChange = (newFilters: AppFilter) => {
        dispatch(setFilter(newFilters, getExchanges));
    };

    useEffect(() => {
        if (exchangesError) {
            dispatch(setToast({
                text: exchangesError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }));

            dispatch(setAbsencesError(''));
        }
    }, [exchangesError]);

    return (
        <ExchangeRequestsContainer
            exchanges={exchanges}
            filter={filter}
            permissions={permissions}
            isLoading={isLoading}
            onFilterChange={onFilterChange}
            onApproveExchange={onApproveExchange}
            onDeclineExchange={onDeclineExchange}
            onApproveShiftExchange={onApproveShiftExchange}
            onDeclineShiftExchange={onDeclineShiftExchange}
            onSearchBarChange={onSearchBarChange}
            onExchangeNavButtonClick={onExchangeNavButtonClick}
        />
    );
};

export default ConnectedExchangeRequests;
